import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon, Send } from 'lucide-react';
import { format } from 'date-fns';

interface QuestionField {
  id: string;
  type: 'text' | 'textarea' | 'date' | 'number';
  label: string;
  required: boolean;
  placeholder?: string;
}

interface QuestionPrompt {
  questions: QuestionField[];
}

interface GibletQuestionFormProps {
  content: string;
  onSubmit: (answers: Record<string, any>) => void;
  isSubmitted?: boolean;
}

export const GibletQuestionForm = ({ content, onSubmit, isSubmitted = false }: GibletQuestionFormProps) => {
  const [answers, setAnswers] = useState<Record<string, any>>({});

  // Extract QUESTION_PROMPT JSON from content
  const extractQuestionPrompt = (text: string): QuestionPrompt | null => {
    try {
      const match = text.match(/QUESTION_PROMPT:\s*({[\s\S]*?})\s*(?:\n|$)/);
      if (match) {
        return JSON.parse(match[1]);
      }
    } catch (e) {
      console.error('Failed to parse question prompt:', e);
    }
    return null;
  };

  const questionPrompt = extractQuestionPrompt(content);
  
  if (!questionPrompt || !questionPrompt.questions) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = questionPrompt.questions
      .filter(q => q.required && !answers[q.id])
      .map(q => q.label);
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    onSubmit(answers);
  };

  const renderField = (field: QuestionField) => {
    const value = answers[field.id];

    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            id={field.id}
            value={value || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            className="min-h-[100px]"
            disabled={isSubmitted}
            required={field.required}
          />
        );

      case 'number':
        return (
          <Input
            id={field.id}
            type="number"
            value={value || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [field.id]: parseFloat(e.target.value) }))}
            placeholder={field.placeholder}
            disabled={isSubmitted}
            required={field.required}
          />
        );

      case 'date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
                disabled={isSubmitted}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), "PPP") : <span>{field.placeholder || "Pick a date"}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => setAnswers(prev => ({ 
                  ...prev, 
                  [field.id]: date ? format(date, 'yyyy-MM-dd') : null 
                }))}
                initialFocus
                className="pointer-events-auto"
                disabled={(date) => date < new Date()}
              />
            </PopoverContent>
          </Popover>
        );

      default: // text
        return (
          <Input
            id={field.id}
            type="text"
            value={value || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            disabled={isSubmitted}
            required={field.required}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4 p-4 border rounded-lg bg-background">
      {questionPrompt.questions.map((field) => (
        <div key={field.id} className="space-y-2">
          <Label htmlFor={field.id}>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderField(field)}
        </div>
      ))}
      
      {!isSubmitted && (
        <Button type="submit" className="w-full gap-2">
          <Send className="h-4 w-4" />
          Submit Answers
        </Button>
      )}
      
      {isSubmitted && (
        <div className="text-sm text-muted-foreground italic">
          Answers submitted. Processing your call...
        </div>
      )}
    </form>
  );
};
